import React, { Component, KeyboardEvent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IVector2 } from "@formant/core/model/IVector2";
import { HStack } from "@formant/ui/component/common/HStack";
import { remap } from "@formant/ui/utils/remap";

import { clamp } from "../../../utils/clamp";
import { roundToDecimalPlaces } from "../../../utils/roundToDecimalPlaces";
import { roundToWholeNumber } from "../../../utils/roundToWholeNumber";
import { NumberField } from "../NumberField";
import { WindowEvent } from "../WindowEvent";
import * as styles from "./index.scss";

export interface IDialProps {
    value: number;
    min: number;
    max: number;
    step?: number;
    hasNumberField?: boolean;
    className?: string;
    rangeClassName?: string;
    numberFieldClassName?: string;
    disabled?: boolean;
    onInputChange: (value: number | undefined) => void;
    onInputBlur?: () => void;
    onSliderChange: (value: number | undefined) => void;
    onMouseUp?: () => void;
}

const sensitivity = 0.75;

@observer
export class Dial extends Component<IDialProps> {
    @observable
    private startCoordinates?: IVector2;
    @observable
    private currentCoordinates?: IVector2;
    private startValue: number = 0;
    private mouseDown: boolean = false;
    private target?: EventTarget;

    public render() {
        const { min, max, value, numberFieldClassName, onInputBlur } =
            this.props;

        const angle = remap(value, min || -1, max || 1, -135, 135);

        const knob = (
            <svg width="51" height="50" viewBox="0 0 51 50">
                <circle cx="25" cy="25" r="25" fill="#3B4668" />
                <mask
                    id="mask0"
                    style={{ maskType: "alpha" }}
                    maskUnits="userSpaceOnUse"
                    x="1"
                    y="0"
                    width="50"
                    height="50"
                >
                    <circle cx="26" cy="25" r="25" fill="#C4C4C4" />
                </mask>
                <g
                    mask="url(#mask0)"
                    style={{
                        transformOrigin: "50% 50%",
                        transformBox: "view-box",
                        transform: `rotate(${angle || 0}deg)`,
                    }}
                >
                    <rect
                        x="18.5"
                        y="-1.5"
                        width="13"
                        height="53"
                        fill="#657197"
                        stroke="#525D7F"
                    />
                    <rect
                        x="23.9727"
                        width="2.05479"
                        height="18.4932"
                        fill="#18D2FF"
                    />
                </g>
            </svg>
        );

        return (
            <HStack className={styles.dial} vAlign="center" hAlign="center">
                <WindowEvent
                    type="touchmove"
                    onEvent={this.onTouchMove}
                    options={{ passive: false }}
                />
                <WindowEvent type="mouseup" onEvent={this.onMouseUp} />
                <WindowEvent type="mousemove" onEvent={this.onMouseMove} />
                <WindowEvent type="touchend" onEvent={this.onTouchEnd} />

                <div
                    className={styles.control}
                    onMouseDown={this.onMouseDown}
                    onTouchStart={this.onTouchStart}
                >
                    {knob}
                </div>

                <NumberField
                    numberValue={value}
                    small={true}
                    className={classnames(styles.field, numberFieldClassName)}
                    onChangeNumber={this.onInputChange}
                    onBlur={onInputBlur}
                    onKeyDown={this.onKeyDown}
                />
            </HStack>
        );
    }

    private onInputChange = (value: number | undefined) => {
        if (value !== undefined) {
            this.props.onInputChange(value);
        }
    };

    private onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && this.props.onInputBlur) {
            this.props.onInputBlur();
        }
    };

    private onMouseDown = (event: React.MouseEvent) => {
        const { value } = this.props;
        const { clientX: x, clientY: y, target } = event;
        this.mouseDown = true;
        this.startCoordinates = { x, y };
        this.startValue = value;
        this.target = target;
    };

    private onMouseUp = () => {
        const { value } = this.props;
        this.mouseDown = false;
        this.startCoordinates = undefined;
        this.currentCoordinates = undefined;
        if (this.target) {
            this.props.onSliderChange(value);
        }
        this.target = undefined;
    };

    private onMouseMove = (event: MouseEvent) => {
        const { clientX: x, clientY: y } = event;
        if (event.button !== 0 || !this.mouseDown) {
            return;
        }

        this.currentCoordinates = { x, y };

        this.setRotationAmount();
    };

    private setRotationAmount() {
        const { min, max, step } = this.props;
        const raw =
            (this.currentCoordinates?.x || 0) - (this.startCoordinates?.x || 0);

        const roundedValue = roundToDecimalPlaces(
            roundToWholeNumber(
                clamp(this.startValue + raw * sensitivity, min, max),
                step || 0.1,
            ),
            2,
        );

        this.props.onSliderChange(roundedValue);
    }

    private onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const { target } = event.touches[0];
        this.target = target;
        this.mouseDown = true;
    };

    private onTouchEnd = () => {
        const { value } = this.props;
        this.mouseDown = false;
        if (this.target) {
            this.props.onSliderChange(value);
        }

        this.target = undefined;
    };

    private onTouchMove = (event: TouchEvent) => {
        const { clientX: x, clientY: y } = event.touches[0];
        if (!this.mouseDown) {
            return;
        }

        this.currentCoordinates = { x, y };

        this.setRotationAmount();
    };
}
